"use client";

import * as api from "@/src/api/sdk.gen";
import { apiFetch } from "@/src/lib/api-fetch";
import { useState, useEffect, useCallback } from "react";
import { UserPlus, Mail, Shield, Clock, CheckCircle, XCircle, Trash2, RefreshCw, Building2 } from "lucide-react";

interface Invitation {
    id: number;
    email?: string | null;
    role?: string | null;
    status?: string | null;
    created_at?: string | null;
}

interface CreateInvitationsResponse {
    invitations: Array<{ organizations: { name: string } }>;
    emailFailed?: boolean;
}

export function InvitationManager() {
    const [invitations, setInvitations] = useState<Invitation[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [email, setEmail] = useState("");
    const [role, setRole] = useState("member");
    const [selectedOrgIds, setSelectedOrgIds] = useState<number[]>([]);
    const [viewingOrgId, setViewingOrgId] = useState("");
    const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
    const [error, setError] = useState<string | null>(null);

    const [adminOrgs, setAdminOrgs] = useState<{ id: number; name: string }[]>([]);
    const [isOrgsLoaded, setIsOrgsLoaded] = useState(false);

    useEffect(() => {
        async function fetchOrgs() {
            try {
                const { data: orgs, error } = await api.listAdminOrganizationsApiV1AdminOrganizationsGet();
                if (!error && orgs) {
                    setAdminOrgs(orgs);
                    if (orgs.length > 0 && !viewingOrgId) {
                        setViewingOrgId(String(orgs[0].id));
                    }
                    if (orgs.length > 0 && selectedOrgIds.length === 0) {
                        setSelectedOrgIds([orgs[0].id]);
                    }
                } else if (error) {
                    setError((error as { error?: string }).error ?? "Failed to load organizations");
                }
            } catch (err) {
                console.error("Failed to fetch admin organizations:", err);
                setError("Failed to load organizations");
            } finally {
                setIsOrgsLoaded(true);
            }
        }
        fetchOrgs();
    }, []);

    const fetchInvitations = useCallback(async () => {
        if (!viewingOrgId) return;
        setIsLoading(true);
        setError(null);
        try {
            const { data, error } = await api.listAdminInvitationsApiV1AdminInvitationsGet({
                query: { org_id: Number(viewingOrgId) },
            });
            if (!error && data) {
                setInvitations(data);
            } else {
                setError((error as { error?: string })?.error ?? "Failed to fetch invitations");
            }
        } catch (err) {
            console.error("Failed to fetch invitations:", err);
            setError("An error occurred while fetching invitations");
        } finally {
            setIsLoading(false);
        }
    }, [viewingOrgId]);

    useEffect(() => {
        fetchInvitations();
    }, [fetchInvitations]);

    const handleInvite = async (e: React.FormEvent) => {
        e.preventDefault();
        if (selectedOrgIds.length === 0) {
            setMessage({ type: "error", text: "Please select at least one organization" });
            return;
        }
        setIsSubmitting(true);
        setMessage(null);

        try {
            const { data: resData, error: reqErr } = await api.createAdminInvitationsApiV1AdminInvitationsPost({
                body: { email, role, orgIds: selectedOrgIds },
            });

            if (!reqErr && resData) {
                const data = resData as unknown as CreateInvitationsResponse;
                const createdOrgNames = data.invitations
                    .map((inv) => inv.organizations.name)
                    .join(", ");
                const emailWarning = data.emailFailed ? " (email delivery failed — user may need a resend)" : "";
                setMessage({
                    type: data.emailFailed ? "error" : "success",
                    text: `Invitation created for ${email} to ${createdOrgNames}${emailWarning}`,
                });
                setEmail("");
                setSelectedOrgIds([]);
                fetchInvitations();
            } else {
                const errObj = (reqErr || {}) as { error?: string };
                setMessage({ type: "error", text: errObj.error || "Failed to send invitation" });
            }
        } catch (err) {
            setMessage({ type: "error", text: "An error occurred" });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleRevoke = async (invitationId: number) => {
        if (!confirm("Are you sure you want to revoke this invitation?")) return;

        try {
            const { error } = await api.revokeInvitationApiV1AdminInvitationsRevokePost({
                body: { invitation_id: invitationId, organization_id: Number(viewingOrgId) },
            });

            if (!error) {
                fetchInvitations();
            } else {
                alert((error as { error?: string }).error ?? "Failed to revoke invitation");
            }
        } catch (err) {
            alert("An error occurred");
        }
    };

    const getStatusBadge = (status: Invitation["status"]) => {
        switch (status) {
            case "pending":
                return (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                        <Clock className="w-3 h-3" /> Pending
                    </span>
                );
            case "accepted":
                return (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircle className="w-3 h-3" /> Accepted
                    </span>
                );
            case "revoked":
                return (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        <XCircle className="w-3 h-3" /> Revoked
                    </span>
                );
        }
    };

    return (
        <div className="space-y-8">
            {/* Invite Form */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
                <h2 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2">
                    <UserPlus className="w-5 h-5 text-indigo-600" />
                    Invite New Member
                </h2>

                <form onSubmit={handleInvite}>
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                        {/* Email */}
                        <div className="relative col-span-1 md:col-span-2">
                            <label className="block text-sm font-bold text-slate-700 mb-1.5">Email Address</label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <input
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="colleague@inara.ai"
                                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none text-sm"
                                />
                            </div>
                        </div>

                        {/* Organizations */}
                        <div className="col-span-1">
                            <label className="block text-sm font-bold text-slate-700 mb-1.5">Organizations</label>
                            <div className="rounded-xl border border-slate-200 p-3 bg-white min-h-[46px]">
                                {!isOrgsLoaded ? (
                                    <p className="text-xs text-slate-400">Loading organizations...</p>
                                ) : adminOrgs.length === 0 ? (
                                    <p className="text-xs text-slate-400">No organizations available</p>
                                ) : (
                                    <div className="space-y-2 max-h-24 overflow-y-auto pr-1">
                                        {adminOrgs.map((org) => {
                                            const checked = selectedOrgIds.includes(org.id);
                                            return (
                                                <label key={org.id} className="flex items-center gap-2 text-sm text-slate-700">
                                                    <input
                                                        type="checkbox"
                                                        checked={checked}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setSelectedOrgIds((prev) => [...prev, org.id]);
                                                            } else {
                                                                setSelectedOrgIds((prev) => prev.filter((id) => id !== org.id));
                                                            }
                                                        }}
                                                        className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                                    />
                                                    <Building2 className="w-3.5 h-3.5 text-slate-400" />
                                                    <span>{org.name}</span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Role */}
                        <div className="col-span-1">
                            <label className="block text-sm font-bold text-slate-700 mb-1.5">Role</label>
                            <div className="relative">
                                <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <select
                                    value={role}
                                    onChange={(e) => setRole(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none text-sm appearance-none bg-white"
                                >
                                    <option value="member">Member (Student)</option>
                                    <option value="admin">Admin (Expert)</option>
                                </select>
                            </div>
                        </div>

                        {/* Submit */}
                        <div className="flex items-end">
                            <button
                                type="submit"
                                disabled={isSubmitting || selectedOrgIds.length === 0}
                                className="w-full bg-indigo-600 text-white rounded-xl px-6 py-2.5 font-bold hover:bg-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-indigo-200"
                            >
                                {isSubmitting ? "Sending..." : "Send Invitation"}
                            </button>
                        </div>
                    </div>
                </form>

                {message && (
                    <div className={`mt-4 p-3 rounded-xl text-sm font-medium ${message.type === "success" ? "bg-green-50 text-green-700 border border-green-100" : "bg-red-50 text-red-700 border border-red-100"}`}>
                        {message.text}
                    </div>
                )}
            </div>

            {/* Invitations Table */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                {error && (
                    <div className="p-6 m-6 bg-amber-50 border border-amber-100 rounded-xl shadow-sm">
                        <div className="flex items-center gap-3 text-amber-800 font-bold mb-3">
                            <Shield className="w-5 h-5 text-amber-600" />
                            Could not load invitations
                        </div>
                        <p className="text-sm text-amber-700 font-medium leading-relaxed">{error}</p>
                        <div className="mt-4">
                            <button
                                onClick={fetchInvitations}
                                className="text-xs font-bold text-indigo-600 hover:text-indigo-700 flex items-center gap-1 bg-white px-3 py-1.5 rounded-lg border border-slate-200 shadow-sm transition-all"
                            >
                                <RefreshCw className="w-3 h-3" /> Try Again
                            </button>
                        </div>
                    </div>
                )}
                <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                    <div className="flex items-center gap-4">
                        <div>
                            <h2 className="text-lg font-bold text-slate-900 tracking-tight">Pending &amp; Recent Invitations</h2>
                            <p className="text-sm text-slate-500 mt-1 font-medium">
                                {viewingOrgId && adminOrgs.find(o => String(o.id) === viewingOrgId)
                                    ? `Showing invitations for ${adminOrgs.find(o => String(o.id) === viewingOrgId)!.name}`
                                    : "Manage and track your organization's outgoing invites"}
                            </p>
                        </div>
                        {adminOrgs.length > 1 && (
                            <select
                                value={viewingOrgId}
                                onChange={(e) => setViewingOrgId(e.target.value)}
                                className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm bg-white"
                            >
                                {adminOrgs.map((org) => (
                                    <option key={org.id} value={String(org.id)}>
                                        {org.name}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>
                    <button
                        onClick={fetchInvitations}
                        disabled={isLoading}
                        className="p-2 text-slate-500 hover:text-indigo-600 hover:bg-white rounded-lg transition-all border border-transparent hover:border-slate-200 shadow-sm disabled:opacity-50"
                    >
                        <RefreshCw className={`w-5 h-5 ${isLoading ? "animate-spin" : ""}`} />
                    </button>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50/50 border-b border-slate-200">
                                <th className="px-6 py-4 text-xs font-extrabold text-slate-900 uppercase tracking-widest">Email</th>
                                <th className="px-6 py-4 text-xs font-extrabold text-slate-900 uppercase tracking-widest">Role</th>
                                <th className="px-6 py-4 text-xs font-extrabold text-slate-900 uppercase tracking-widest">Status</th>
                                <th className="px-6 py-4 text-xs font-extrabold text-slate-900 uppercase tracking-widest text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {isLoading ? (
                                <tr>
                                    <td colSpan={4} className="px-6 py-12 text-center text-slate-400 font-medium">
                                        <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-3 opacity-20" />
                                        Loading invitations...
                                    </td>
                                </tr>
                            ) : invitations.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="px-6 py-12 text-center text-slate-400 font-medium italic">
                                        No invitations found
                                    </td>
                                </tr>
                            ) : (
                                invitations.map((invite) => (
                                    <tr key={invite.id} className="hover:bg-slate-50/50 transition-colors group">
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col">
                                                <span className="text-sm font-bold text-slate-900">{invite.email}</span>
                                                {invite.created_at && (
                                                    <span className="text-[10px] text-slate-400 font-bold uppercase tracking-tighter">Sent {new Date(invite.created_at).toLocaleDateString()}</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="text-sm font-semibold text-slate-700 capitalize">
                                                {invite.role}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">{getStatusBadge(invite.status)}</td>
                                        <td className="px-6 py-4 text-right">
                                            {invite.status === "pending" && (
                                                <button
                                                    onClick={() => handleRevoke(invite.id)}
                                                    className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all opacity-0 group-hover:opacity-100 flex items-center gap-1.5 ml-auto text-xs font-bold"
                                                >
                                                    <Trash2 className="w-4 h-4" /> Revoke
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
